import { ref, shallowRef, onUnmounted, watch } from 'vue'
import {
  LocalAudioTrack,
  createLocalAudioTrack,
  RoomEvent,
  Room,
  ConnectionState,
  Track,
} from 'livekit-client'
import type {
  RemoteAudioTrack,
  RemoteTrackPublication,
  RemoteParticipant,
  RemoteTrack,
} from 'livekit-client'
import {
  useLivekitCore,
  DIABOLOCOM_RTC_CONFIG,
  useLivekitContext,
  useLivekitAudio,
  useLivekitConnection,
  useLivekit,
} from '@/composables/livekit'
import fetchApi from '@/utils/fetchApi'
import { captureSentryException } from '@/utils/sentry'

interface TestConnectResponse {
  wsUrl: string
  token: string
  roomName: string
}

interface ConnectOptions {
  configId?: string
}

interface ExtendedRoom extends Room {
  listenersAttachedLiveTest?: boolean
}

// Module-level singleton state (shared across all component instances)
const isConnecting = ref(false)
const isConnected = ref(false)
const callEnded = ref(false)
const error = ref<string | null>(null)
const durationSec = ref(0)
const isMuted = ref(false)
const localAudioTrack = shallowRef<LocalAudioTrack | null>(null)
let durationInterval: ReturnType<typeof setInterval> | null = null

export const useLiveTest = () => {
  const { room, connected } = useLivekitCore()
  const { transcripts, tools, lifecycleEvents, resetContext } = useLivekitContext()
  const audioComposable = useLivekitAudio()
  const { endCall } = useLivekitConnection()

  // Initialize useLivekit to ensure RPC handler is registered
  useLivekit()

  const startDurationTimer = () => {
    durationSec.value = 0
    durationInterval = setInterval(() => {
      durationSec.value++
    }, 1000)
  }

  const stopDurationTimer = () => {
    if (durationInterval) {
      clearInterval(durationInterval)
      durationInterval = null
    }
  }

  const handleCallEnded = async () => {
    stopDurationTimer()
    callEnded.value = true
    isConnected.value = false
    connected.value = false

    // Cleanup local audio track
    if (localAudioTrack.value) {
      localAudioTrack.value.stop()
      localAudioTrack.value = null
    }

    // Cleanup remote audio
    audioComposable.cleanupAll()

    // Disconnect from room to avoid DataChannel errors
    if (room.state === ConnectionState.Connected) {
      await room.disconnect()
    }
  }

  const setupRoomListeners = () => {
    const extendedRoom = room as ExtendedRoom
    if (extendedRoom.listenersAttachedLiveTest) return
    extendedRoom.listenersAttachedLiveTest = true

    // RPC method 'updateChatContext' is registered in useLiveKit.ts
    // No need to duplicate here - both use the same singleton room and context

    room.on(
      RoomEvent.TrackSubscribed,
      (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        if (track.kind === Track.Kind.Audio) {
          audioComposable.attachAudioTrack(track as RemoteAudioTrack, participant)
        }
      },
    )

    room.on(RoomEvent.TrackUnsubscribed, (_track, _pub, participant) => {
      audioComposable.detachAudioTrack(participant)
    })

    // Listen for participant disconnection (call ended)
    room.on(RoomEvent.ParticipantDisconnected, () => {
      void handleCallEnded()
    })

    // Also listen for room disconnect (in case agent closes the room)
    room.on(RoomEvent.Disconnected, () => {
      void handleCallEnded()
    })
  }

  setupRoomListeners()

  // Watch lifecycle events to start/stop timer
  watch(
    lifecycleEvents,
    (events) => {
      const hasCallStarted = events.some((e) => e.type === 'call_started')
      const hasCallEnded = events.some((e) => e.type === 'call_ended')

      if (hasCallStarted && !durationInterval) {
        startDurationTimer()
      }

      if (hasCallEnded) {
        stopDurationTimer()
      }
    },
    { deep: true },
  )

  // Wrapper that calls endCall RPC and ensures timer stops
  const endCallWithCleanup = async () => {
    await endCall()
    // Ensure timer stops and state is updated even if ParticipantDisconnected doesn't fire
    await handleCallEnded()
  }

  const toggleMute = async () => {
    if (!localAudioTrack.value) return
    if (isMuted.value) {
      await localAudioTrack.value.unmute()
    } else {
      await localAudioTrack.value.mute()
    }
    isMuted.value = !isMuted.value
  }

  const connect = async (options: ConnectOptions) => {
    const { configId } = options

    if (room.state === ConnectionState.Connected) {
      return true
    }

    if (!configId) {
      error.value = 'configId is required'
      return false
    }

    isConnecting.value = true
    error.value = null
    callEnded.value = false
    durationSec.value = 0
    resetContext()
    try {
      // Request microphone permission and create local audio track
      localAudioTrack.value = await createLocalAudioTrack({
        echoCancellation: true,
        noiseSuppression: true,
      })

      // Call BFF to get test room token
      const response = await fetchApi<TestConnectResponse>({
        endpoint: '/api/livekit/test-connect',
        method: 'POST',
        body: { configId },
      })

      if (!response.token || !response.wsUrl) {
        throw new Error('Invalid response from test-connect endpoint')
      }

      const { wsUrl, token } = response

      // Connect to room
      await room.connect(wsUrl, token, { autoSubscribe: true, rtcConfig: DIABOLOCOM_RTC_CONFIG })

      // Publish local audio track
      await room.localParticipant.publishTrack(localAudioTrack.value)

      connected.value = true
      isConnected.value = true
      isConnecting.value = false

      // Timer will be started by call_started lifecycle event

      return true
    } catch (err) {
      void captureSentryException(err, {
        tags: { component: 'useLiveTest', operation: 'connect' },
        extra: { configId },
      })

      // Check if it's a microphone permission error
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        error.value = 'microphone_permission_denied'
      } else {
        error.value = (err as Error).message || 'connection_failed'
      }

      isConnecting.value = false
      return false
    }
  }

  const disconnect = async () => {
    stopDurationTimer()

    // Stop and cleanup local audio track
    if (localAudioTrack.value) {
      localAudioTrack.value.stop()
      localAudioTrack.value = null
    }

    // Cleanup remote audio
    audioComposable.cleanupAll()

    // Disconnect from room
    if (room.state === ConnectionState.Connected) {
      await room.disconnect()
    }

    connected.value = false
    isConnected.value = false

    // Reset context and state
    resetContext()
    durationSec.value = 0
    callEnded.value = false
  }

  // Cleanup on unmount
  onUnmounted(() => {
    void disconnect()
  })

  return {
    // State
    isConnecting,
    isConnected,
    callEnded,
    error,
    durationSec,
    transcripts,
    tools,
    isMuted,
    localAudioTrack,

    // Actions
    connect,
    disconnect,
    endCall: endCallWithCleanup,
    toggleMute,

    // Audio (for LivekitAudioPlayer)
    audio: audioComposable,
  }
}
